import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Pencil, Plus, Loader2, Copy, Globe, Search, X, Archive, ArchiveRestore, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const MyCreatedLOIs = () => {
  usePageTitle('My Created LOIs');
  const { user } = useAuth();
  const { adminTheme } = useTheme();
  const [lois, setLois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortHeader = ({ field, children }) => {
    const isActive = sortBy === field;
    return (
      <button
        onClick={() => handleSort(field)}
        className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors font-medium"
      >
        {children}
        {isActive ? (
          sortOrder === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
        )}
      </button>
    );
  };

  const fetchLois = async () => {
    try {
      setLoading(true);
      const params = { archived: showArchived, sort_by: sortBy, sort_order: sortOrder };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/loi-creation/my-lois', { params });
      setLois(Array.isArray(res.data) ? res.data : res.data.lois || []);
    } catch (err) {
      toast.error('Failed to load LOIs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLois();
  }, [showArchived, statusFilter, sortBy, sortOrder]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLois();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleDownload = async (loiId, buyerName) => {
    try {
      const res = await api.get(`/loi-creation/${loiId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LOI-${buyerName || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download LOI');
    }
  };

  const handleDuplicate = async (loiId) => {
    try {
      const res = await api.post(`/loi-creation/${loiId}/duplicate`);
      toast.success(`LOI duplicated! New LOI: ${res.data.loi_number}`);
      fetchLois();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate LOI');
    }
  };

  const handleToggleNetwork = async (loi) => {
    const loiId = loi._id || loi.id;
    const isPosted = loi.network_posted;
    try {
      if (isPosted) {
        await api.post(`/loi-creation/${loiId}/remove-from-network`);
        toast.success('LOI removed from the Global Trados Network');
      } else {
        await api.post(`/loi-creation/${loiId}/post-to-network`);
        toast.success('LOI posted to the Global Trados Network!');
      }
      fetchLois();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update network status');
    }
  };

  const handleToggleArchive = async (loi) => {
    const loiId = loi._id || loi.id;
    const isArchived = loi.archived;
    try {
      await api.post(`/loi-creation/${loiId}/archive`);
      toast.success(isArchived ? 'LOI unarchived' : 'LOI archived');
      fetchLois();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update LOI');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Draft', variant: 'secondary' },
      completed: { label: 'Completed', variant: 'default' },
      verified: { label: 'Verified', variant: 'default' },
      pending: { label: 'Pending', variant: 'outline' },
    };
    const s = statusMap[status] || { label: status || 'Unknown', variant: 'secondary' };
    return <Badge variant={s.variant}>{s.label}</Badge>;
  };

  const hasActiveFilters = searchQuery.trim() || statusFilter;

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      );
    }

    if (lois.length === 0 && !hasActiveFilters && !showArchived) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No LOIs Created Yet</h3>
          <p className="text-sm text-gray-500 mb-6">You haven't created any LOIs yet.</p>
          <Link
            to="/create-loi"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2B4B6A] text-white rounded-lg font-semibold hover:bg-[#3a6590] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First LOI &rarr;
          </Link>
        </div>
      );
    }

    if (lois.length === 0 && (hasActiveFilters || showArchived)) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto flex items-center justify-center mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No LOIs Found</h3>
          <p className="text-sm text-gray-500 mb-4">
            {showArchived ? 'No archived LOIs match your criteria.' : 'No LOIs match your search or filter criteria.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead><SortHeader field="loi_number">LOI Number</SortHeader></TableHead>
              <TableHead><SortHeader field="seller">Seller</SortHeader></TableHead>
              <TableHead><SortHeader field="commodity">Commodity</SortHeader></TableHead>
              <TableHead><SortHeader field="total_value">Total Value</SortHeader></TableHead>
              <TableHead><SortHeader field="created_at">Created</SortHeader></TableHead>
              <TableHead><SortHeader field="status">Status</SortHeader></TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lois.map((loi) => (
              <TableRow key={loi._id || loi.id} className={loi.archived ? 'opacity-60' : ''}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium">{loi.loi_number || 'N/A'}</span>
                    {loi.network_posted && (
                      <span title="Posted to Global Trados Network">
                        <Globe className="w-3.5 h-3.5 text-green-500" />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{loi.seller_name || loi.seller?.company_name || 'N/A'}</TableCell>
                <TableCell>{loi.commodity || loi.product?.commodity_type || 'N/A'}</TableCell>
                <TableCell>{formatCurrency(loi.total_value || loi.terms?.total_value)}</TableCell>
                <TableCell>{formatDate(loi.created_at)}</TableCell>
                <TableCell>{getStatusBadge(loi.status)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => handleDownload(loi._id || loi.id, loi.buyer?.company_name || loi.buyer_name)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Download PDF"
                    >
                      <Download className="w-3.5 h-3.5" />
                      PDF
                    </button>
                    {!loi.archived && (
                      <>
                        <Link
                          to={`/create-loi?edit=${loi._id || loi.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-[#2B4B6A] bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          title="Edit LOI"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Link>
                        <button
                          onClick={() => handleDuplicate(loi._id || loi.id)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                          title="Duplicate LOI"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleToggleNetwork(loi)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            loi.network_posted
                              ? 'text-orange-700 bg-orange-50 hover:bg-orange-100'
                              : 'text-green-700 bg-green-50 hover:bg-green-100'
                          }`}
                          title={loi.network_posted ? 'Remove from Network' : 'Post to Trados Network'}
                        >
                          <Globe className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleToggleArchive(loi)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        loi.archived
                          ? 'text-blue-700 bg-blue-50 hover:bg-blue-100'
                          : 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                      }`}
                      title={loi.archived ? 'Unarchive LOI' : 'Archive LOI'}
                    >
                      {loi.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {showArchived ? 'Archived LOIs' : 'My Created LOIs'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {showArchived ? 'View your archived Letters of Intent' : 'View and manage your Letters of Intent'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                showArchived
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Show Active' : 'Archived'}
            </button>
            {!showArchived && (
              <Link
                to="/create-loi"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2B4B6A] text-white rounded-lg font-semibold hover:bg-[#3a6590] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create LOI
              </Link>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by LOI number, seller, buyer, commodity..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B4B6A]/20 focus:border-[#2B4B6A] bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2B4B6A]/20 focus:border-[#2B4B6A] bg-white appearance-none cursor-pointer min-w-[160px]"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="completed">Completed</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {renderContent()}
      </div>
    </Sidebar>
  );
};

export default MyCreatedLOIs;
